/**
 * FaceAuthenticationModal.tsx
 *
 * Face-verified authentication for the Capacitor app — two modes sharing
 * the same camera/challenge/verify pipeline:
 *
 *   mode='recovery' (default): locked-out or failing users prove identity
 *     with their enrolled face template on a known device and immediately
 *     set a new password — no OTP delivery needed.
 *   mode='login': face is a login factor. A successful scan mints a
 *     single-use face_login token which the parent submits through the
 *     normal /auth/login flow (MFA still applies downstream).
 *
 * Flow:
 *   1. 'challenge'— fetch a server-issued random action sequence from
 *                  /api/v1/auth/face-challenge (blink / head-turn). The
 *                  server verifies the submitted sequence — a replayed
 *                  video can't perform actions it never recorded.
 *   2. 'scan'    — camera + MediaPipe detection + challenge actions, then
 *                  face-api.js descriptor capture.
 *   3. 'verify'  — POST /api/v1/auth/face-recovery or /face-login
 *                  (descriptor + device_id/app_id + challenge evidence).
 *   4. 'password'/'done' — recovery continues to the reset; login hands
 *                  the token to the parent via onLoginSuccess.
 */

'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ScanFace, CheckCircle, AlertCircle, Loader2, KeyRound } from 'lucide-react';
import { useFacialRecognition } from '@/hooks/useFacialRecognition';
import { useMlKitFaceDetection } from '@/hooks/useMlKitFaceDetection';
import { useStableBiometricCaptureState } from '@/components/attendance/hooks/useStableBiometricCaptureState';
import { useHoldStillGate } from '@/components/attendance/hooks/useHoldStillGate';
import BiometricCaptureView, { statusPillClass } from '@/components/attendance/components/BiometricCaptureView';
import { loadFaceApiModels } from '@/components/attendance/services/faceApiLoader';
import { extractFaceApiDescriptor, drawFaceOverlay, clearCanvas, captureVideoFrame } from '@/components/attendance/services/mediaProcessor';
import { debouncedPasswordValidation } from '@/lib/passwordValidation';
import type { PasswordValidationResult } from '@/lib/passwordValidation';
import { buildApiUrl, isCapacitor } from '@/lib/platformUtils';
import capacitorDeviceService from '@/lib/capacitorDeviceService';
import { requestIntegrityToken, type IntegrityTokenBundle } from '@/lib/playIntegrityClient';
import { useChallengeMotion } from '@/hooks/useChallengeMotion';
import { useTheme } from '@/contexts/ThemeContext';
import CapacitorModalFilterHeader from '@/components/CapacitorModalFilterHeader';

type RecoveryStep = 'scan' | 'verifying' | 'matched' | 'password' | 'done' | 'error';
type FaceAuthMode = 'recovery' | 'login';
type ChallengeAction = 'blink' | 'turn';

const MAX_VERIFY_ATTEMPTS = 3;
const TURN_YAW_THRESHOLD_DEG = 12;
/** Consecutive published frames the yaw must hold past threshold — single
 *  noisy frames shouldn't satisfy a turn. */
const TURN_HOLD_FRAMES = 2;
/** Per-challenge deadline for the user to finish the action sequence before
 *  a fresh one is fetched. */
const CHALLENGE_EXPIRY_GRACE_MS = 500;

interface FaceAuthenticationModalProps {
  isOpen: boolean;
  /** The email/phone the user typed on the login form */
  contact: string;
  /** 'recovery' mints a password-reset code; 'login' mints a face_login
   *  token the parent submits through the normal login flow. */
  mode?: FaceAuthMode;
  onClose: () => void;
  /** Called after the new password has been saved — parent should clear any
   *  local lockout state and show a success message. `mfaRequired` reports
   *  whether the next sign-in will still demand an MFA challenge. */
  onSuccess: (mfaRequired: boolean) => void;
  /** login mode only — called with the minted login token + canonical
   *  contact; the parent submits the normal /auth/login request. */
  onLoginSuccess?: (loginToken: string, contact: string) => void;
  /** Optional header overrides — lets a caller carry its own context
   *  (e.g. "Approve Windows sign-in — Gavin → DESKTOP") instead of the
   *  generic sign-in copy. */
  title?: string;
  subtitle?: string;
}

interface FaceAuthResponse {
  success: boolean;
  matched?: boolean;
  recovery_code?: string;
  login_token?: string;
  contact?: string;
  error?: string;
}

interface ActiveChallenge {
  id: string;
  actions: ChallengeAction[];
  /** Date.now() deadline for completing the sequence. */
  expiresAt: number;
}

async function waitForVideoFrames(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= 2) return;
  await new Promise<void>((resolve, reject) => {
    let settled = false;
    let timeoutId = 0;
    let onReady: () => void = () => {};
    const cleanup = () => {
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('playing', onReady);
      window.clearTimeout(timeoutId);
    };
    onReady = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };
    timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Camera video did not start in time. Please try again.'));
    }, 8_000);
    video.addEventListener('loadeddata', onReady, { once: true });
    video.addEventListener('canplay', onReady, { once: true });
    video.addEventListener('playing', onReady, { once: true });
  });
}

export default function FaceAuthenticationModal({ isOpen, contact, mode = 'recovery', onClose, onSuccess, onLoginSuccess, title, subtitle }: FaceAuthenticationModalProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';
  /** Native app: camera steps render as a full-screen panel with shared
   *  modal chrome instead of the centered web card. */
  const capacitorShell = isCapacitor();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const initGenerationRef = useRef(0);
  const initStartedRef = useRef(false);
  const autoCapturePausedRef = useRef(false);
  const livenessLatchedRef = useRef(false);

  // ── Server-issued liveness challenge ─────────────────────────────────
  // The server randomizes an action sequence (blink/turn) stored in Redis;
  // evidence submitted with the descriptor must match it in order.
  const challengeRef = useRef<ActiveChallenge | null>(null);
  const challengeStepRef = useRef(0);
  const challengeEvidenceRef = useRef<Array<{ type: string; offset_ms: number }>>([]);
  const challengeBaselineBlinkRef = useRef(0);
  const turnHoldFramesRef = useRef(0);
  const challengeT0Ref = useRef(0);
  /** True until the executor records the pre-challenge blink count as the
   *  first action's baseline (a blink before issue mustn't satisfy it). */
  const challengeBaselinePendingRef = useRef(true);
  /** Whether the blendshape engine started — decides if blink actions are
   *  issuable. Captured when the challenge is fetched. */
  const challengeSupportsBlinkRef = useRef(true);
  /** Play Integrity token minted alongside the challenge — rides the
   *  face-login/recovery body so the server can attest the binary is the
   *  genuine Play-installed app (invalid token = tampered client). */
  const integrityBundleRef = useRef<IntegrityTokenBundle | null>(null);
  // Gyro/accel sampler running while the challenge executes — binds the
  // actions to a physically moving handheld device. start/stop are stable
  // callbacks, safe to reference inside fetchChallenge's deps.
  const { start: startChallengeMotion, stop: stopChallengeMotion } = useChallengeMotion();
  const [challengeActions, setChallengeActions] = useState<ChallengeAction[] | null>(null);
  const [challengeStepIdx, setChallengeStepIdx] = useState(0);
  const [challengeDone, setChallengeDone] = useState(false);
  const [challengeLoading, setChallengeLoading] = useState(false);

  const [step, setStep] = useState<RecoveryStep>('scan');
  const [initializing, setInitializing] = useState(true);
  const [statusMessage, setStatusMessage] = useState('Initializing camera...');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [verifyAttempts, setVerifyAttempts] = useState(0);

  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const [recoveryContact, setRecoveryContact] = useState<string>(contact);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult>({ isValid: true });
  const [mfaRequired, setMfaRequired] = useState(false);

  const wasmEngine = useFacialRecognition();
  const mlkitEngine = useMlKitFaceDetection();
  // On native builds the on-device ML Kit pipeline drives detection —
  // real eye-open probabilities and head Euler angles satisfy the server
  // challenge far more reliably than the WASM blendshape thresholds. If the
  // native path proves broken on this device we fall back to WASM.
  const useNativeEngine = mlkitEngine.isSupported && !mlkitEngine.nativeFailed;
  const {
    isInitialized,
    currentDetection,
    initializeCamera,
    initializeDetector,
    startDetection,
    stopDetection,
    stopCamera,
    openCameraSettings,
    enableLiveness,
  } = useNativeEngine ? mlkitEngine : wasmEngine;

  const isStale = useCallback((generation: number) => generation !== initGenerationRef.current || !isOpen, [isOpen]);

  // ── Server-issued liveness challenge ─────────────────────────────────
  const fetchChallenge = useCallback(async (supportsBlink: boolean) => {
    setChallengeLoading(true);
    setChallengeDone(false);
    setChallengeActions(null);
    setChallengeStepIdx(0);
    challengeRef.current = null;
    challengeStepRef.current = 0;
    challengeEvidenceRef.current = [];
    turnHoldFramesRef.current = 0;

    try {
      const deviceInfo = capacitorDeviceService.getDeviceInfoForAPI();
      const response = await fetch(buildApiUrl('/api/v1/auth/face-challenge'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact,
          device_id: deviceInfo.device_id ?? null,
          app_id: deviceInfo.app_id ?? null,
          supports_blink: supportsBlink,
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success || !Array.isArray(data.actions)) {
        throw new Error('challenge fetch failed');
      }
      challengeT0Ref.current = performance.now();
      challengeRef.current = {
        id: data.challenge_id,
        actions: data.actions as ChallengeAction[],
        expiresAt: Date.now() + (data.ttl_seconds ?? 75) * 1000 - CHALLENGE_EXPIRY_GRACE_MS,
      };
      challengeBaselinePendingRef.current = true;
      setChallengeActions(data.actions as ChallengeAction[]);
      setStatusMessage('Follow the prompts to prove it\u2019s you');
      startChallengeMotion();
      // Mint the integrity token while the user performs the actions —
      // by submit time it's resolved, adding no latency to the flow.
      integrityBundleRef.current = null;
      void requestIntegrityToken(mode === 'login' ? 'face-login' : 'face-recovery', { contact })
        .then((bundle) => { integrityBundleRef.current = bundle; });
    } catch (error) {
      console.error('[FaceAuth] Challenge fetch failed:', error);
      setErrorMessage('Could not start verification. Check your connection and try again.');
    } finally {
      setChallengeLoading(false);
    }
  }, [contact, mode, startChallengeMotion]);

  const fetchChallengeRef = useRef(fetchChallenge);
  fetchChallengeRef.current = fetchChallenge;

  /** Reset local challenge state and fetch a fresh sequence — the server
   *  consumes each challenge on verify, so every retry needs a new one. */
  const resetChallengeForRetry = useCallback(() => {
    challengeRef.current = null;
    setChallengeActions(null);
    setChallengeDone(false);
    setChallengeStepIdx(0);
    challengeStepRef.current = 0;
    challengeEvidenceRef.current = [];
    turnHoldFramesRef.current = 0;
    void fetchChallengeRef.current(challengeSupportsBlinkRef.current);
  }, []);

  // Advance the challenge sequence on each published detection frame.
  // 'blink' watches the blendshape blink counter (baseline at action start);
  // 'turn' watches real head yaw from facial keypoints — either direction
  // satisfies it (direction adds nothing to replay resistance and front
  // cameras mirror inconsistently).
  useEffect(() => {
    const challenge = challengeRef.current;
    if (!challenge || challengeDone || step !== 'scan' || !currentDetection?.detected) return;

    if (Date.now() > challenge.expiresAt) {
      void fetchChallengeRef.current(challengeSupportsBlinkRef.current);
      return;
    }

    const action = challenge.actions[challengeStepRef.current];
    if (!action) return;

    // First frame of a fresh challenge: latch the blink counter as the
    // baseline so a blink performed before issue can't satisfy the action.
    if (challengeBaselinePendingRef.current) {
      challengeBaselinePendingRef.current = false;
      challengeBaselineBlinkRef.current = currentDetection.liveness?.blinkCount ?? 0;
    }

    let completed = false;
    if (action === 'blink') {
      completed = (currentDetection.liveness?.blinkCount ?? 0) > challengeBaselineBlinkRef.current;
    } else {
      const yaw = currentDetection.metrics?.pose?.yaw ?? 0;
      turnHoldFramesRef.current = Math.abs(yaw) >= TURN_YAW_THRESHOLD_DEG
        ? turnHoldFramesRef.current + 1
        : 0;
      completed = turnHoldFramesRef.current >= TURN_HOLD_FRAMES;
    }

    if (completed) {
      challengeEvidenceRef.current.push({
        type: action,
        offset_ms: Math.round(performance.now() - challengeT0Ref.current),
      });
      challengeStepRef.current += 1;
      challengeBaselineBlinkRef.current = currentDetection.liveness?.blinkCount ?? 0;
      turnHoldFramesRef.current = 0;
      setChallengeStepIdx(challengeStepRef.current);
      if (challengeStepRef.current >= challenge.actions.length) {
        setChallengeDone(true);
        setStatusMessage('Hold still — capturing');
      }
    }
  }, [currentDetection, challengeDone, step]);

  // ── Camera + model initialization ──────────────────────────────────────
  const initCamera = useCallback(async () => {
    // Guard: the effect that calls this re-runs when `isInitialized` flips
    // after detector setup — without this, the camera initializes twice.
    if (initStartedRef.current) return;
    initStartedRef.current = true;
    const generation = ++initGenerationRef.current;
    try {
      setInitializing(true);
      setErrorMessage(null);
      setStatusMessage('Loading face recognition models...');

      const modelsLoaded = await loadFaceApiModels();
      if (isStale(generation)) return;
      if (!modelsLoaded) {
        throw new Error('Face recognition models failed to load. Please check your connection and try again.');
      }

      if (!videoRef.current || !canvasRef.current) {
        throw new Error('Camera view was not ready. Please close and reopen.');
      }

      setStatusMessage('Initializing camera...');
      await initializeCamera(videoRef.current);
      if (isStale(generation)) return;

      await waitForVideoFrames(videoRef.current);
      if (isStale(generation)) return;

      if (!isInitialized) {
        setStatusMessage('Initializing face detection...');
        await initializeDetector();
        if (isStale(generation)) return;
      }

      setStatusMessage('Center your face inside the guide');
      startDetection(canvasRef.current);

      // Blendshape liveness is needed for the server challenge — enable it
      // before issuing so supports_blink reflects actual capability.
      try {
        challengeSupportsBlinkRef.current = await enableLiveness();
      } catch {
        challengeSupportsBlinkRef.current = false;
      }
      if (isStale(generation)) return;
      void fetchChallenge(challengeSupportsBlinkRef.current);

      setInitializing(false);
    } catch (error) {
      if (isStale(generation)) return;
      console.error('[FaceAuth] Init failed:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to initialize camera');
      setStatusMessage('Camera initialization failed');
      setStep('error');
      setInitializing(false);
    }
  }, [isStale, isInitialized, initializeCamera, initializeDetector, startDetection, enableLiveness, fetchChallenge]);

  // Native ML Kit engine died mid-scan — release its camera and let the
  // lifecycle effect re-init with the WASM pipeline instead of leaving the
  // user staring at a dead preview. Declared before the lifecycle effect so
  // it resets the init guard first.
  const mlkitFailed = mlkitEngine.nativeFailed;
  const mlkitStopDetection = mlkitEngine.stopDetection;
  const mlkitStopCamera = mlkitEngine.stopCamera;
  useEffect(() => {
    if (!mlkitFailed || !isOpen || step !== 'scan') return;
    console.warn('[FaceAuth] ML Kit engine failed — falling back to WASM pipeline');
    initStartedRef.current = false;
    mlkitStopDetection();
    mlkitStopCamera();
    setErrorMessage(null);
    setStatusMessage('Restarting face detection...');
  }, [mlkitFailed, isOpen, step, mlkitStopDetection, mlkitStopCamera]);

  // Open/close lifecycle — always reacquire the camera on open, release on close.
  useEffect(() => {
    if (!isOpen || step !== 'scan') return;

    let attempts = 0;
    const tryInit = () => {
      if (videoRef.current && canvasRef.current) {
        void initCamera();
        return;
      }
      attempts += 1;
      if (attempts < 5) {
        window.setTimeout(tryInit, 150);
      } else {
        void initCamera();
      }
    };
    const timer = window.setTimeout(tryInit, 100);
    return () => window.clearTimeout(timer);
  }, [isOpen, step, initCamera]);

  // Leaving the camera steps unmounts the video element — release the
  // stream + detection loop so the camera isn't held during password entry.
  useEffect(() => {
    if (!isOpen) return;
    if (step === 'scan' || step === 'verifying') return;
    initGenerationRef.current += 1;
    stopDetection();
    stopCamera();
  }, [isOpen, step, stopDetection, stopCamera]);

  useEffect(() => {
    if (isOpen) return;
    initGenerationRef.current += 1;
    initStartedRef.current = false;
    autoCapturePausedRef.current = false;
    livenessLatchedRef.current = false;
    challengeRef.current = null;
    challengeStepRef.current = 0;
    challengeEvidenceRef.current = [];
    turnHoldFramesRef.current = 0;
    challengeBaselinePendingRef.current = true;
    challengeSupportsBlinkRef.current = true;
    setChallengeActions(null);
    setChallengeStepIdx(0);
    setChallengeDone(false);
    setChallengeLoading(false);
    stopDetection();
    stopCamera();
    setStep('scan');
    setInitializing(true);
    setErrorMessage(null);
    setVerifyAttempts(0);
    setRecoveryCode(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  }, [isOpen, stopDetection, stopCamera]);

  // Draw the tracked-face overlay each detection frame.
  useEffect(() => {
    if (!overlayCanvasRef.current || !isInitialized || !isOpen) return;
    const overlay = overlayCanvasRef.current;
    if (currentDetection) {
      clearCanvas(overlay);
      drawFaceOverlay(overlay, currentDetection, false);
    } else {
      clearCanvas(overlay);
    }
  }, [currentDetection, isInitialized, isOpen]);

  // ── Liveness + capture state ───────────────────────────────────────────
  const detection = currentDetection;
  // Liveness latches: micro-movement is computed on the trailing frames, so
  // the moment the user holds still to be captured it flips off — a user who
  // already proved movement shouldn't lose it. Once live, stay live until
  // the modal closes/retries.
  const frameLiveness = Boolean(
    detection?.liveness && (detection.liveness.blinkCount >= 1 || detection.liveness.microMovementDetected)
  );
  if (frameLiveness) livenessLatchedRef.current = true;
  const livenessOk = livenessLatchedRef.current;

  const rawState = (() => {
    if (!detection?.detected) return 'searching' as const;
    if (detection.distance?.status === 'too_close') return 'move_back' as const;
    if (detection.distance?.status === 'too_far') return 'move_closer' as const;
    if (detection.lighting?.status === 'too_dark') return 'improve_lighting' as const;
    return detection.readyToCapture ? ('ready' as const) : ('detected' as const);
  })();

  const { displayState: captureState, displayMessage } = useStableBiometricCaptureState({
    rawState,
    message: statusMessage,
    readyHoldMs: 1500,
    detectedHoldMs: 700,
  });

  // Capture requires the server challenge to be completed — the issued
  // action sequence IS the liveness proof (a blink or turn in the recorded
  // evidence implies livenessOk anyway).
  const canCapture = Boolean(detection?.detected && detection.readyToCapture && challengeDone);

  // Hold-still gate: the frame must stay capture-ready through the flash
  // countdown before verification fires — no more capturing off a frame
  // where the face only just landed. The challenge is this session's
  // liveness and always completes first (`canCapture` needs challengeDone),
  // so the countdown can never overlap the prompts.
  const {
    armed: captureArmed,
    flashPulse: holdFlashPulse,
  } = useHoldStillGate(isOpen && step === 'scan' && !initializing && canCapture);

  // Refs for the auto-capture timer — avoids stale closures re-firing.
  const captureArmedRef = useRef(captureArmed);
  captureArmedRef.current = captureArmed;
  const stepRef = useRef(step);
  stepRef.current = step;
  const handleVerifyRef = useRef<(() => void) | null>(null);

  // ── Capture + server verification ──────────────────────────────────────
  const handleVerify = useCallback(async () => {
    if (!videoRef.current || submitting || stepRef.current !== 'scan') return;

    setStep('verifying');
    setErrorMessage(null);
    setStatusMessage('Checking your face...');

    try {
      // Strict mode first — enrolled templates were generated without the
      // bounds-assist path, so descriptors must be produced the same way.
      let template = await extractFaceApiDescriptor(videoRef.current);
      if (!template && detection?.bounds) {
        template = await extractFaceApiDescriptor(videoRef.current, detection.bounds);
      }
      if (!template) {
        setStep('scan');
        setStatusMessage('No face detected — center your face and improve lighting');
        // Descriptor extraction failing shouldn't spin a tight retry loop
        autoCapturePausedRef.current = true;
        window.setTimeout(() => { autoCapturePausedRef.current = false; }, 1500);
        return;
      }

      let capturedImage: string | null = null;
      try {
        capturedImage = captureVideoFrame(videoRef.current);
      } catch { /* audit image is best-effort */ }

      const deviceInfo = capacitorDeviceService.getDeviceInfoForAPI();
      const endpoint = mode === 'login' ? '/api/v1/auth/face-login' : '/api/v1/auth/face-recovery';
      const response = await fetch(buildApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact,
          device_id: deviceInfo.device_id ?? null,
          app_id: deviceInfo.app_id ?? null,
          platform: deviceInfo.platform ?? null,
          descriptor: Array.from(template),
          challenge_id: challengeRef.current?.id,
          evidence: {
            completed: challengeEvidenceRef.current,
            motion: stopChallengeMotion() ?? undefined,
          },
          integrity_token: integrityBundleRef.current?.integrityToken,
          integrity_request_hash: integrityBundleRef.current?.requestHash,
          integrity_package: integrityBundleRef.current?.packageName,
          captured_image: capturedImage,
          liveness: {
            blinkCount: detection?.liveness?.blinkCount ?? 0,
            // Latched across the scan session — the instantaneous flag flips
            // off when the user holds still for capture.
            microMovementDetected: livenessOk || Boolean(detection?.liveness?.microMovementDetected),
            confidence: livenessOk ? 'high' : 'low',
          },
        }),
      });

      const data: FaceAuthResponse = await response.json().catch(() => ({ success: false }));

      if (mode === 'login' && response.ok && data.success && data.matched && data.login_token) {
        setStep('matched');
        setStatusMessage('Face verified');
        window.setTimeout(() => {
          onLoginSuccess?.(data.login_token!, data.contact || contact);
        }, 900);
        return;
      }

      if (mode === 'recovery' && response.ok && data.success && data.matched && data.recovery_code) {
        setRecoveryCode(data.recovery_code);
        setRecoveryContact(data.contact || contact);
        // Flash a clear "verified" state so the user sees the scan worked
        // before the password form replaces the camera view.
        setStep('matched');
        setStatusMessage('Face verified');
        window.setTimeout(() => {
          if (stepRef.current === 'matched') {
            setStep('password');
            setStatusMessage('Identity verified — set a new password');
          }
        }, 1200);
        return;
      }

      const attempts = verifyAttempts + 1;
      setVerifyAttempts(attempts);
      if (attempts >= MAX_VERIFY_ATTEMPTS) {
        setStep('error');
        setErrorMessage(mode === 'login'
          ? 'We could not verify your face after several attempts. Please sign in with your password instead.'
          : 'We could not verify your face after several attempts. Please use the password reset link instead.');
        return;
      }
      setStep('scan');
      setErrorMessage(data.error || 'We could not verify your face on this device. Trying again...');
      setStatusMessage('Center your face inside the guide');
      resetChallengeForRetry();
      // Pause auto-capture briefly so the user sees the error before retrying
      autoCapturePausedRef.current = true;
      window.setTimeout(() => { autoCapturePausedRef.current = false; }, 1800);
    } catch (error) {
      console.error('[FaceAuth] Verify failed:', error);
      const attempts = verifyAttempts + 1;
      setVerifyAttempts(attempts);
      if (attempts >= MAX_VERIFY_ATTEMPTS) {
        setStep('error');
        setErrorMessage(mode === 'login'
          ? 'We could not verify your face after several attempts. Please sign in with your password instead.'
          : 'We could not verify your face after several attempts. Please use the password reset link instead.');
        return;
      }
      setStep('scan');
      setErrorMessage(error instanceof Error ? error.message : 'Verification failed. Trying again...');
      setStatusMessage('Center your face inside the guide');
      resetChallengeForRetry();
      autoCapturePausedRef.current = true;
      window.setTimeout(() => { autoCapturePausedRef.current = false; }, 1800);
    }
  }, [contact, detection, livenessOk, mode, submitting, verifyAttempts, onLoginSuccess, resetChallengeForRetry, stopChallengeMotion]);

  handleVerifyRef.current = handleVerify;

  // Auto-capture: once the face has been held ready through the hold-still
  // flash countdown, fire verification (matches the attendance modal's auto
  // mode — the target users shouldn't have to find a button). Pauses briefly
  // after each failed attempt; gives up after MAX_VERIFY_ATTEMPTS.
  useEffect(() => {
    if (!isOpen || step !== 'scan' || initializing || !captureArmed || autoCapturePausedRef.current) return;
    if (verifyAttempts >= MAX_VERIFY_ATTEMPTS) {
      setStep('error');
      setErrorMessage(mode === 'login'
        ? 'We could not verify your face after several attempts. Please sign in with your password instead.'
        : 'We could not verify your face after several attempts. Please use the password reset link instead.');
      return;
    }
    // The gate already proved ~1.5s of sustained readiness — the short beat
    // just lets the final flash land before the frame is taken.
    const timer = window.setTimeout(() => {
      if (captureArmedRef.current && stepRef.current === 'scan' && !autoCapturePausedRef.current) {
        void handleVerifyRef.current?.();
      }
    }, 150);
    return () => window.clearTimeout(timer);
  }, [isOpen, step, initializing, captureArmed, verifyAttempts, mode]);

  // ── Set new password (uses the standard reset endpoint + code) ─────────
  // Same validation pattern as the signup page — debounced into state.
  useEffect(() => {
    if (newPassword) {
      debouncedPasswordValidation(newPassword, setPasswordValidation);
    } else {
      setPasswordValidation({ isValid: true });
    }
    return () => {
      if (globalThis.__passwordValidationTimeout) {
        clearTimeout(globalThis.__passwordValidationTimeout);
      }
    };
  }, [newPassword]);

  const passwordsMatch = newPassword === confirmPassword;
  const passwordReady = newPassword.length > 0 && passwordValidation.isValid && passwordsMatch;

  const handleSetPassword = useCallback(async () => {
    if (!recoveryCode || !passwordReady || submitting) return;

    setSubmitting(true);
    setPasswordError(null);
    try {
      const response = await fetch(buildApiUrl('/api/v1/auth/verify-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact: recoveryContact,
          code: recoveryCode,
          newPassword,
          purpose: 'password_reset',
        }),
      });
      const data = await response.json().catch(() => ({ success: false }));

      if (response.ok && data.success) {
        setMfaRequired(Boolean(data.mfa_required));
        setStep('done');
        return;
      }
      setPasswordError(data.error || 'Could not save your new password. Please try again.');
    } catch (error) {
      setPasswordError(error instanceof Error ? error.message : 'Could not save your new password.');
    } finally {
      setSubmitting(false);
    }
  }, [recoveryCode, recoveryContact, newPassword, passwordReady, submitting]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9998]">
      {(step === 'matched' || step === 'password' || step === 'done') ? (
        /* Full-screen auth layout — same shell as login / signup / reset-password */
        <div className="h-full min-h-screen overflow-y-auto flex items-center justify-center px-3 py-4 sm:p-4 pm-gradient-hero">
          <div className="pm-card relative max-w-md w-full p-4 sm:p-6 md:p-8">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 z-10 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="text-center mb-6 sm:mb-8">
              <div className="bg-gradient-to-br from-teal-500 to-blue-600 w-14 h-14 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                {step === 'password' ? (
                  <KeyRound className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
                ) : (
                  <CheckCircle className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
                )}
              </div>
              <h1 className="pm-auth-heading mb-2">
                {step === 'matched' ? 'Face verified' : step === 'done' ? 'All done' : 'Set a new password'}
              </h1>
              <p className="pm-text-secondary text-sm">
                {step === 'matched'
                  ? 'We know it\'s you — let\'s secure your account.'
                  : step === 'done'
                    ? 'Your password has been updated.'
                    : 'Your face is verified. Choose a new password to finish.'}
              </p>
            </div>

            {step === 'matched' && (
              <div className="flex items-center justify-center gap-2 text-sm pm-text-secondary">
                <Loader2 className="h-4 w-4 animate-spin" /> {mode === 'login' ? 'Signing you in...' : 'Loading password form...'}
              </div>
            )}

            {step === 'password' && (
              <form onSubmit={(e) => { e.preventDefault(); void handleSetPassword(); }} className="space-y-3 sm:space-y-4">
                {passwordError && (
                  <div className="pm-alert pm-alert-error mb-4">
                    {passwordError}
                  </div>
                )}

                {/* Password — identical fields to the signup page */}
                <div>
                  <label htmlFor="password" className="pm-label">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setPasswordError(null);
                      }}
                      className="pm-input pr-10"
                      placeholder="Password"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                      onClick={() => setShowPassword(v => !v)}
                    >
                      {showPassword ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {/* Password validation message */}
                  {passwordValidation.message && (
                    <div className="mt-1 text-sm text-red-600">
                      {passwordValidation.message}
                    </div>
                  )}
                  {!passwordValidation.message && newPassword && passwordValidation.isValid && (
                    <div className="mt-1 text-sm text-green-600">
                      ✓ Password meets requirements
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirmPassword" className="pm-label">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setPasswordError(null);
                      }}
                      className="pm-input pr-10"
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                      onClick={() => setShowConfirmPassword(v => !v)}
                    >
                      {showConfirmPassword ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {/* Password match validation */}
                  {confirmPassword && !passwordsMatch && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Passwords do not match
                    </div>
                  )}
                  {confirmPassword && passwordsMatch && (
                    <div className="mt-1 text-sm text-green-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Passwords match
                    </div>
                  )}
                </div>

                <div>
                  <button
                    type="submit"
                    className="pm-btn-primary w-full disabled:opacity-50"
                    disabled={submitting || !passwordReady}
                  >
                    {submitting ? 'Saving...' : 'Save new password'}
                  </button>
                </div>
              </form>
            )}

            {step === 'done' && (
              <>
                <p className="pm-text-secondary text-sm text-center mb-6">
                  Your password has been updated and your account is unlocked.
                  Sign in with your new password.
                  {mfaRequired && ' For security, you will also be asked to complete multi-factor authentication.'}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    onSuccess(mfaRequired);
                    onClose();
                  }}
                  className="pm-btn-primary w-full"
                >
                  Back to Sign In
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
      <div
        className={capacitorShell
          ? `flex h-full flex-col ${isDark ? 'bg-gray-900' : 'bg-white'}`
          : 'h-full flex items-center justify-center bg-black/70 p-4'}
        style={capacitorShell ? { paddingBottom: 'env(safe-area-inset-bottom)' } : undefined}
      >
      {capacitorShell && (
        <CapacitorModalFilterHeader
          icon={ScanFace}
          title={title ?? (mode === 'login' ? 'Sign in with your face' : 'Verify it\'s you')}
          subtitle={subtitle ?? (mode === 'login'
            ? 'Look at the camera and follow the prompts'
            : 'This device knows you — follow the prompts to prove it')}
          isDark={isDark}
          onClose={onClose}
          applyTopSafeArea
          showDivider={false}
        />
      )}
      <div className={capacitorShell
        ? 'flex min-h-0 flex-1 flex-col overflow-y-auto'
        : 'w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden'}>
        {/* Header — web card only (Capacitor uses CapacitorModalFilterHeader above) */}
        {!capacitorShell && (
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <ScanFace className="h-5 w-5 text-blue-600" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {title ?? 'Verify it\'s you'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        )}

        <div className={capacitorShell
          ? 'mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 py-4'
          : 'p-5'}>
          {(step === 'scan' || step === 'verifying') && (() => {
            // One message feeds both banners — the active liveness
            // instruction takes priority so top and bottom always agree.
            const challengeInstruction = step === 'scan' && !initializing && !challengeDone
              && challengeActions && challengeStepIdx < challengeActions.length
              ? (challengeActions[challengeStepIdx] === 'blink'
                ? 'Blink your eyes'
                : 'Turn your head left or right')
              : null;
            const bannerMessage = step === 'verifying'
              ? 'Checking your face...'
              : challengeInstruction ?? (initializing ? statusMessage : displayMessage);
            const bannerState = step === 'verifying' ? 'ready' : captureState;
            const banner = (
              <div className={`rounded-xl p-2.5 text-center transition-all duration-500 ease-out ${statusPillClass(bannerState)}`}>
                <p className="text-[13px] font-medium text-white sm:text-sm">{bannerMessage}</p>
              </div>
            );

            return (
            <div className="space-y-4">
              {/* Live status banners — identical pill above and below the
                  camera so the current instruction is visible wherever the
                  user's eyes are on screen. */}
              {banner}

              <BiometricCaptureView
                videoRef={videoRef}
                canvasRef={canvasRef}
                overlayCanvasRef={overlayCanvasRef}
                detection={detection}
                detected={Boolean(detection?.detected)}
                ready={Boolean(canCapture)}
                captureState={step === 'verifying' ? 'ready' : captureState}
                holdFlashPulse={holdFlashPulse}
                initializing={initializing}
                initMessage={statusMessage}
                showMeters={false}
              />

              {banner}

              {errorMessage && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="flex-1">{errorMessage}</span>
                  {!challengeActions && !challengeLoading && step === 'scan' && (
                    <button
                      type="button"
                      onClick={() => {
                        setErrorMessage(null);
                        void fetchChallenge(challengeSupportsBlinkRef.current);
                      }}
                      className="shrink-0 font-semibold underline"
                    >
                      Retry
                    </button>
                  )}
                </div>
              )}

              {/* Challenge progress dots — the instruction text itself lives
                  in the identical banners above/below the camera. */}
              {step === 'scan' && !initializing && !challengeDone && (
                challengeLoading ? (
                  <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Starting verification…
                  </div>
                ) : challengeActions && challengeStepIdx < challengeActions.length ? (
                  <div className="flex justify-center gap-1.5">
                    {challengeActions.map((_, i) => (
                      <span
                        key={i}
                        className={`h-1.5 w-7 rounded-full transition-colors ${
                          i < challengeStepIdx ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-700'
                        }`}
                      />
                    ))}
                  </div>
                ) : null
              )}
            </div>
            );
          })()}

          {step === 'error' && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{errorMessage || 'Something went wrong.'}</span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setVerifyAttempts(0);
                    autoCapturePausedRef.current = false;
                    // Camera was released when we left the scan steps — the
                    // 'scan' effect re-inits it, guarded by initStartedRef.
                    initStartedRef.current = false;
                    setStep('scan');
                  }}
                  className="flex-1 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  Try again
                </button>
                <button
                  type="button"
                  onClick={openCameraSettings}
                  className="flex-1 rounded-xl border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-700 dark:border-gray-600 dark:text-gray-300"
                >
                  Camera settings
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
      )}
    </div>,
    document.body
  );
}
